import { apiEsign } from "@/api/esign.api.ts";
import { useDownloadHelper } from "@/hooks/use-download.helper.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EsignDocumentRow } from "@/pages/esign/widgets/EsignDocumentRow.tsx";
import { avatarSizeToNumberMap } from "@/types/employees/employee.types.ts";
import { ApiEsignResult, EsignRequestStatus, EsignSignatureType } from "@/types/esign.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  Avatar,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  ProgressBar,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface EsignResultDetailProps {
  esignResult: ApiEsignResult;
  documentId: number;
  documentTitle: string;
  onShowCertificate: (envelopeId: string, requestPublicId: string) => void;
}

export function EsignResultDetail({
  esignResult,
  documentId,
  documentTitle,
  onShowCertificate,
}: EsignResultDetailProps) {
  const { t } = useTranslation();
  const { renderShortDate } = useFormatter();
  const { downloadFile } = useDownloadHelper();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const canDownloadReport =
    isFeatureEnabled(ApiFeatureType.esign) && canIEdit(ApiPermission.esign_download_report);

  const isSignature = esignResult.signature_type === EsignSignatureType.signature;
  const signedOfKey = isSignature ? "core:esign_signed_of" : "core:esign_acknowledged_of";

  const signedCount = esignResult.signers.filter(
    (signer) => signer.status === EsignRequestStatus.signed
  ).length;
  const totalCount = esignResult.signers.length;
  const progressPercent = totalCount === 0 ? 0 : Math.round((signedCount / totalCount) * 100);

  const handleDownloadReport = (requestPublicId: string) => {
    setDownloadingId(requestPublicId);
    downloadFile({
      apiCall: () => apiEsign.getEvidenceReportPdf(esignResult.envelope_public_id),
      onFinally: () => setDownloadingId(null),
    });
  };

  const detailsConfig = [
    { label: t("common:created"), value: renderShortDate(esignResult.datetime_created_at) },
    {
      label: t("common:due_date"),
      value: esignResult.date_due ? renderShortDate(esignResult.date_due) : "—",
    },
  ];

  return (
    <>
      <Stack gap="xs">
        <Title header={t("core:esign_personas")} size="xs" />
        <Stack gap="sm" className="mt-4">
          <Stack direction="horizontal" items="center" justify="between">
            <Text className="text-title">
              {t(signedOfKey, { signed: signedCount, total: totalCount })}
            </Text>
            <Text className="text-secondary">{progressPercent}%</Text>
          </Stack>
          <ProgressBar
            completed={signedCount}
            total={totalCount}
            variant="status"
            tooltip={t(signedOfKey, { signed: signedCount, total: totalCount })}
          />
        </Stack>
      </Stack>

      <Stack gap="xs">
        <Title header={t("core:esign_signers")} size="xs" />
        <Stack>
          {esignResult.signers.map((signer) => {
            const isSigned = signer.status === EsignRequestStatus.signed;
            return (
              <ListItem
                key={signer.request_public_id}
                icon={
                  <Avatar
                    url={signer.avatar_url?.replace("{size}", avatarSizeToNumberMap.md.toString())}
                    acronym={signer.full_name}
                    size="md"
                  />
                }
                title={signer.full_name}
                captionSlot={
                  <Text className={isSigned ? "text-positive" : "text-warning"}>
                    {isSigned
                      ? isSignature
                        ? t("core:esign_status_signed")
                        : t("core:esign_status_acknowledged")
                      : t("core:esign_status_pending")}
                  </Text>
                }
                valueSlot={
                  isSigned && canDownloadReport ? (
                    <DropdownMenu
                      trigger={
                        <ButtonIcon
                          variant="secondary"
                          icon={<FontAwesomeIcon icon="fa-light fa-ellipsis" />}
                        />
                      }
                    >
                      <DropdownMenuItem
                        onClick={() =>
                          onShowCertificate(
                            esignResult.envelope_public_id,
                            signer.request_public_id
                          )
                        }
                        title={t(
                          isSignature
                            ? "core:esign_show_signature_certificate"
                            : "core:esign_show_acknowledgment_certificate"
                        )}
                        leftIcon={<FontAwesomeIcon icon="fa-light fa-file-certificate" />}
                      />
                      <DropdownMenuItem
                        onClick={() => handleDownloadReport(signer.request_public_id)}
                        disabled={downloadingId === signer.request_public_id}
                        title={t("core:esign_download_report")}
                        leftIcon={
                          <FontAwesomeIcon
                            icon={
                              downloadingId === signer.request_public_id
                                ? "fa-light fa-spinner"
                                : "fa-light fa-cloud-arrow-down"
                            }
                            className={
                              downloadingId === signer.request_public_id
                                ? "animate-[spin_7s_linear_infinite]"
                                : undefined
                            }
                          />
                        }
                      />
                    </DropdownMenu>
                  ) : undefined
                }
              />
            );
          })}
        </Stack>
      </Stack>

      <EsignDocumentRow documentId={documentId} documentTitle={documentTitle} showFields={false} />

      <Stack>
        <Title header={t("common:details")} size="xs" />
        {detailsConfig.map((row) => (
          <ListItem key={row.label} title={row.label} caption={row.value ?? "-"} />
        ))}
      </Stack>
    </>
  );
}
